import type { ServiceRequest } from "../types";
import { dueUrgency, isPromised, operativeDate } from "../urgency";
import { formatDate } from "../utils";
import { useT } from "../i18n";

/**
 * A date alone does not tell you whether to worry. The chip does, and the exact
 * date stays beside it because "in 3 days" is not something you can put in a diary.
 *
 * Reads the PROMISED date when Prismic has committed to one, and says so — the
 * client's original ask is context after that, not the thing being tracked.
 *
 * Lives here rather than in one page because the overview and the request list show
 * the SAME field: two copies would drift, and the first screenshot of this feature
 * caught exactly that — chips on one screen, bare dates on the other.
 */
export default function DueCell({ request }: { request: Pick<ServiceRequest, "due_date" | "promised_date" | "status"> }) {
  const t = useT();
  const date = operativeDate(request);
  const promised = isPromised(request);
  const urgency = dueUrgency(date, request.status);
  if (urgency.level === "none") return <span className="due-none">{t("common.notSet")}</span>;
  return (
    <div className="due-cell">
      <span className={`due-chip due-${urgency.level}`}>{urgency.label}</span>
      <span className="due-date">
        {promised ? `${t("due.promised")} ` : ""}{formatDate(date)}
      </span>
      {/* Only worth saying when the two disagree. */}
      {promised && request.due_date && request.due_date !== request.promised_date && (
        <span className="due-asked">asked for {formatDate(request.due_date)}</span>
      )}
    </div>
  );
}
